/**
 * CoffeePassAnalyticsTab - Comprehensive Bula Coffee Pass analytics
 * Redemption patterns, upgrade rates, and revenue insights
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Coffee, TrendingUp, DollarSign, Users, BarChart3, 
  Calendar, RefreshCw, ArrowUpRight, Award, Package,
  PieChart, ShoppingBag
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '../../../utils/apiClient';

export default function CoffeePassAnalyticsTab() {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/analytics/coffee-pass-redemptions?period=${period}`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch coffee pass analytics:', err);
      toast.error('Failed to load analytics');
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!data) return null;

  const { summary, revenue, context, daily_breakdown, top_extras, insights } = data;

  // Calculate percentages for pie chart visualization
  const justCoffeePct = summary?.just_coffee_pct || 0;
  const upgradedPct = summary?.upgraded_pct || 0;
  const extrasPct = summary?.bought_extras_pct || 0;

  return (
    <div className="space-y-6" data-testid="coffee-pass-analytics">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Coffee className="w-7 h-7 text-amber-500" />
            Bula Coffee Pass Analytics
          </h2>
          <p className="text-muted-foreground">Understand how pass members use their benefits</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[140px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Insights Banner */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-200 dark:border-amber-800">
        <CardContent className="p-6">
          <h3 className="font-bold text-lg text-amber-800 dark:text-amber-200 mb-3">Key Insights - {data.period_label}</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-700 dark:text-amber-300">Conversion Rate</p>
                <p className="text-xl font-bold text-amber-900 dark:text-amber-100">{insights?.conversion_rate}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-orange-500 flex items-center justify-center">
                <ArrowUpRight className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-orange-700 dark:text-orange-300">Upgrade Rate</p>
                <p className="text-xl font-bold text-orange-900 dark:text-orange-100">{insights?.upgrade_rate}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-sm text-green-700 dark:text-green-300">Avg Extra Spend</p>
                <p className="text-xl font-bold text-green-900 dark:text-green-100">{insights?.avg_extra_spend}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Total Redemptions</p>
              <Coffee className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-3xl font-bold">{summary?.total_redemptions || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              ~{summary?.avg_redemptions_per_day || 0}/day avg
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Extra Revenue</p>
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">${revenue?.total_extra_revenue || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              ${revenue?.avg_extra_per_redemption || 0} per visit
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Active Passes</p>
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold">{context?.active_passes || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {context?.total_passes_sold || 0} total sold
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Bought Extras</p>
              <Package className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold">{summary?.bought_extras_count || 0}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {summary?.bought_extras_pct || 0}% of redemptions
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Behavior Breakdown */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Redemption Behavior Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="w-5 h-5 text-amber-500" />
              Redemption Behavior
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Just Coffee */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Just Free Coffee (Nothing Else)</span>
                  <span className="font-medium">{summary?.just_coffee_count || 0} ({justCoffeePct}%)</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gray-400 rounded-full transition-all duration-500"
                    style={{ width: `${justCoffeePct}%` }}
                  />
                </div>
              </div>
              
              {/* Upgraded */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Upgraded to Large</span>
                  <span className="font-medium">{summary?.upgraded_count || 0} ({upgradedPct}%)</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${upgradedPct}%` }}
                  />
                </div>
              </div>
              
              {/* Bought Extras */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span>Ordered Additional Items</span>
                  <span className="font-medium">{summary?.bought_extras_count || 0} ({extrasPct}%)</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${extrasPct}%` }}
                  />
                </div>
              </div>
            </div>
            
            {/* Revenue Breakdown */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium mb-3">Revenue Breakdown</h4>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">Upgrades</p>
                  <p className="text-lg font-bold text-orange-600">${revenue?.upgrade_revenue || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">Add-ons</p>
                  <p className="text-lg font-bold text-purple-600">${revenue?.addon_revenue || 0}</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xs text-muted-foreground">Other Items</p>
                  <p className="text-lg font-bold text-green-600">${revenue?.other_items_revenue || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Top Extras Purchased */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-green-500" />
              Top Items Purchased by Pass Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_extras && top_extras.length > 0 ? (
              <div className="space-y-3">
                {top_extras.map((item, idx) => (
                  <div 
                    key={item.item}
                    className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg"
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      idx < 3 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    }`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{item.item}</p>
                    </div>
                    <p className="font-bold">{item.quantity} sold</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No extra items purchased yet</p>
              </div>
            )}
            
            {/* Avg Spend */}
            <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700 dark:text-green-300">Avg Spend per Buyer</p>
                  <p className="text-2xl font-bold text-green-800 dark:text-green-200">
                    ${revenue?.avg_extra_per_buyer || 0}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                When pass members buy extras, this is their average spend
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-500" />
            Daily Redemption Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          {daily_breakdown && daily_breakdown.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-right py-2 px-3">Redemptions</th>
                    <th className="text-right py-2 px-3">Just Coffee</th>
                    <th className="text-right py-2 px-3">Upgraded</th>
                    <th className="text-right py-2 px-3">Bought Extras</th>
                    <th className="text-right py-2 px-3">Extra Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {daily_breakdown.slice(0, 14).map((day) => (
                    <tr key={day.date} className="border-b hover:bg-muted/30">
                      <td className="py-2 px-3 font-medium">{day.date}</td>
                      <td className="py-2 px-3 text-right">{day.redemptions}</td>
                      <td className="py-2 px-3 text-right text-gray-500">{day.just_coffee}</td>
                      <td className="py-2 px-3 text-right text-orange-600">{day.upgraded}</td>
                      <td className="py-2 px-3 text-right text-green-600">{day.bought_extras}</td>
                      <td className="py-2 px-3 text-right font-medium text-green-600">
                        ${day.extra_revenue?.toFixed(2) || '0.00'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No redemption data for this period</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
